import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, RotateCcw, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "How do I post an opening?",
  "How do I accept a booking?",
  "Tips to get more bookings",
];

function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

function RichText({ text }) {
  return (
    <div className="grid gap-1.5">
      {text.split("\n").filter((l) => l.trim()).map((line, i) => {
        const num = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
        const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
        if (num)
          return (
            <div key={i} className="flex gap-2">
              <span className="mono shrink-0 text-[#8A7333]">{num[1]}.</span>
              <span>{renderInline(num[2])}</span>
            </div>
          );
        if (bullet)
          return (
            <div key={i} className="flex gap-2">
              <span className="shrink-0 text-[#C9A85F]">•</span>
              <span>{renderInline(bullet[1])}</span>
            </div>
          );
        return <p key={i}>{renderInline(line.replace(/^#+\s*/, ""))}</p>;
      })}
    </div>
  );
}

function Bubble({ role, content, testId }) {
  const mine = role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={testId}>
      <div
        className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
          mine ? "bg-[#2A2724] text-[#F7F3EC]" : "border border-border bg-[#FBF8F2] text-foreground"
        }`}
      >
        {mine ? content : <RichText text={content} />}
      </div>
    </div>
  );
}

async function streamChat(message, onDelta) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bella/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.detail || "Bella couldn't answer just now.");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop();
    for (const ev of events) {
      const line = ev.trim();
      if (!line.startsWith("data:")) continue;
      const data = JSON.parse(line.slice(5));
      if (data.error) throw new Error(data.error);
      if (data.delta) onDelta(data.delta);
    }
  }
}

function ChatPanel({ onClose }) {
  const [greeting, setGreeting] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api
      .get("/bella/history")
      .then(({ data }) => {
        setGreeting(data.greeting);
        setMessages(data.messages || []);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      await streamChat(msg, (delta) =>
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: last.content + delta };
          return copy;
        })
      );
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: e.message };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const newChat = async () => {
    try {
      await api.delete("/bella/history");
      setMessages([]);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      data-testid="bella-panel"
      className="bella-enter fixed bottom-24 right-4 z-[1200] flex h-[min(600px,calc(100vh-8rem))] w-[calc(100vw-2rem)] max-w-[400px] flex-col border border-[#C9A85F] bg-background shadow-[0_24px_60px_-20px_rgba(42,39,36,0.45)] sm:right-6"
    >
      <div className="flex items-center justify-between border-b border-border bg-[#2A2724] px-4 py-3 text-[#F7F3EC]">
        <div className="flex items-center gap-2.5">
          <span className="gold-metallic-bg flex h-8 w-8 items-center justify-center text-[#2A2724]">
            <Sparkles size={15} />
          </span>
          <div>
            <p className="text-sm font-semibold">Bella AI</p>
            <p className="mono text-[9px] uppercase tracking-[0.25em] text-[#C9A85F]">Your Book Away guide</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={newChat} data-testid="bella-new-chat-btn" title="New chat" className="p-2 text-[#F7F3EC]/70 transition-colors hover:text-[#C9A85F]">
            <RotateCcw size={15} />
          </button>
          <button type="button" onClick={onClose} data-testid="bella-close-btn" title="Close" className="p-2 text-[#F7F3EC]/70 transition-colors hover:text-[#C9A85F]">
            <X size={17} />
          </button>
        </div>
      </div>

      <div ref={scrollRef} data-testid="bella-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {greeting && <Bubble role="assistant" content={greeting} testId="bella-greeting" />}
        {messages.length === 0 && greeting && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="bella-suggestions">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={s}
                type="button"
                data-testid={`bella-suggestion-${i}`}
                onClick={() => send(s)}
                className="border border-[#C9A85F] px-3 py-1.5 text-xs text-[#8A7333] transition-colors hover:bg-[#C9A85F]/10"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) =>
          m.role === "assistant" && !m.content ? (
            <div key={i} data-testid="bella-typing" className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 size={13} className="animate-spin text-[#C9A85F]" /> Bella is typing…
            </div>
          ) : (
            <Bubble key={i} role={m.role} content={m.content} testId={`bella-msg-${i}`} />
          )
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="flex items-center gap-2 border-t border-border p-3"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Bella anything…"
          maxLength={2000}
          data-testid="bella-input"
          className="h-10 flex-1 border border-border bg-transparent px-3 text-sm outline-none focus:border-[#C9A85F]"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="bella-send-btn"
          className="gold-metallic-bg flex h-10 w-10 items-center justify-center text-[#2A2724] transition-opacity disabled:opacity-50"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </form>
    </div>
  );
}

export const BellaAssistant = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  if (user?.role !== "business" || !pathname.startsWith("/dashboard")) return null;

  return (
    <>
      {open && <ChatPanel onClose={() => setOpen(false)} />}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="bella-launcher-btn"
        aria-label={open ? "Close Bella AI" : "Chat with Bella AI"}
        className="gold-metallic-bg fixed bottom-5 right-4 z-[1200] flex h-14 items-center gap-2 rounded-full px-5 text-[#2A2724] shadow-[0_12px_30px_-10px_rgba(42,39,36,0.6)] transition-transform hover:-translate-y-0.5 sm:right-6"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
        <span className="text-xs font-semibold uppercase tracking-widest">{open ? "Close" : "Ask Bella"}</span>
      </button>
    </>
  );
};
